import { useEffect, useState, useCallback } from "react";
import { DateField } from "@/components/ui/date-field";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { fmtEur, fmtDate, monthLabel } from "@/lib/format";
import { LogOut, FileText, Banknote, Wallet, CalendarClock, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  paid_ontime: { l: "Betaald", c: "bg-emerald-100 text-emerald-700" },
  paid_late: { l: "Betaald (te laat)", c: "bg-amber-100 text-amber-700" },
  partial: { l: "Deels betaald", c: "bg-orange-100 text-orange-700" },
  open: { l: "Openstaand", c: "bg-slate-100 text-slate-600" },
  late: { l: "Te laat", c: "bg-red-100 text-red-700" },
};
const kindLabel = (k) => (k === "lening" ? "Lening" : k === "factuur" ? "Factuur" : "Huur");

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [announceFor, setAnnounceFor] = useState(null);
  const [form, setForm] = useState({ period: "", amount: "", date: new Date().toISOString().slice(0, 10), note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/portal/me");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/portaal", { replace: true });
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const logout = async () => { try { await api.post("/portal/logout"); } catch {} navigate("/portaal", { replace: true }); };

  const [paying, setPaying] = useState(null);
  const payOnline = async (p) => {
    setPaying(p.id);
    try {
      const r = await api.post("/portal/pay", { payment_id: p.id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Betaling starten mislukt"); setPaying(null); }
  };
  const payable = (s) => ["open", "late", "partial"].includes(s);

  const openAnnounce = (debtor) => { setAnnounceFor(debtor); setForm({ period: "", amount: "", date: new Date().toISOString().slice(0, 10), note: "" }); };
  const submitAnnounce = async () => {
    if (!(Number(form.amount) > 0)) return toast.error("Vul een geldig bedrag in");
    setBusy(true);
    try {
      const r = await api.post("/portal/announce-payment", { tenant_id: announceFor.id, ...form, amount: Number(form.amount) });
      toast.success(r.data?.message || "Betaling gemeld");
      setAnnounceFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Melden mislukt"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-400">Laden…</div>;

  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      <div className="bg-[#0F1E3D] border-b-2 border-[#C9A227]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-white p-0.5 flex items-center justify-center ring-1 ring-[#C9A227]/60"><img src="/logo.png" alt="" className="h-full w-full object-contain" /></div>
            <div><div className="font-heading font-bold text-white text-sm">Debiteurenportaal</div><div className="text-xs text-[#C9A227]">{data.email}</div></div>
          </div>
          <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10" onClick={logout} data-testid="portal-logout-button"><LogOut className="h-4 w-4 mr-1" /> Uitloggen</Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {data.debtors.length === 0 && <Card className="p-8 text-center text-slate-500">Er zijn geen dossiers gekoppeld aan dit e-mailadres.</Card>}
        {data.debtors.map((d) => {
          const sorted = [...d.payments].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
          const openTermijnen = sorted.filter((p) => ["open", "late", "partial"].includes(p.status));
          const nextDue = openTermijnen[0] || null;
          const isOverdue = nextDue && (nextDue.due_date || "") < new Date().toISOString().slice(0, 10);
          return (
            <Card key={d.id} className="p-6 border-t-4 border-t-[#0F1E3D]" data-testid={`portal-debtor-${d.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-heading text-lg font-bold text-slate-900">{d.name}</h2>
                  <p className="text-sm text-slate-500">{[d.property_label, d.client_name].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => openAnnounce(d)} data-testid={`portal-announce-open-${d.id}`}><Wallet className="h-4 w-4 mr-1" /> Betaling melden</Button>
              </div>

              {/* Samenvatting: openstaand saldo + eerstvolgende vervaldatum */}
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div className={`rounded-xl border p-4 ${d.outstanding > 0 ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`} data-testid={`portal-summary-outstanding-${d.id}`}>
                  <div className="text-xs font-medium text-slate-500 mb-1">Openstaand saldo (incl. boete)</div>
                  <div className={`font-heading text-2xl font-bold ${d.outstanding > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid={`portal-outstanding-${d.id}`}>{fmtEur(d.outstanding)}</div>
                  {d.outstanding > 0 ? <div className="text-xs text-red-600/80 mt-1">Er staat nog een bedrag open.</div> : <div className="text-xs text-emerald-700/80 mt-1">Alles is voldaan. Bedankt!</div>}
                </div>
                <div className={`rounded-xl border p-4 flex items-start gap-3 ${nextDue ? (isOverdue ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50") : "border-emerald-200 bg-emerald-50"}`} data-testid={`portal-summary-next-${d.id}`}>
                  <CalendarClock className={`h-5 w-5 mt-0.5 shrink-0 ${nextDue ? (isOverdue ? "text-red-500" : "text-slate-400") : "text-emerald-500"}`} />
                  <div>
                    <div className="text-xs font-medium text-slate-500 mb-1">Eerstvolgende vervaldatum</div>
                    {nextDue ? (
                      <>
                        <div className={`font-heading text-2xl font-bold ${isOverdue ? "text-red-600" : "text-slate-800"}`} data-testid={`portal-next-due-${d.id}`}>{fmtDate(nextDue.due_date)}</div>
                        <div className="text-xs text-slate-500 mt-1">{monthLabel(nextDue.period)} · {fmtEur(nextDue.amount)}{isOverdue ? " · verlopen" : ""}</div>
                      </>
                    ) : (
                      <div className="font-heading text-lg font-bold text-emerald-600">Geen openstaande termijnen</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contracten */}
              {d.contracts.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs font-semibold text-slate-500 mb-2">Overeenkomsten</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {d.contracts.map((c) => (
                      <div key={c.id} className="rounded-lg border p-3 text-sm" data-testid={`portal-contract-${c.id}`}>
                        <div className="flex items-center gap-2 mb-1"><Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{kindLabel(c.kind)}</Badge>{c.ovk_number && <span className="text-xs font-mono text-slate-400">{c.ovk_number}</span>}</div>
                        {c.kind === "lening" ? (
                          <div className="text-slate-600">Hoofdsom {fmtEur(c.total_amount)} · {c.term_months} mnd · {c.interest_rate}%</div>
                        ) : (
                          <div className="text-slate-600">Maandbedrag {fmtEur(c.monthly_rent)}</div>
                        )}
                        <div className="text-xs text-slate-400 mt-1">Vanaf {fmtDate(c.start_date)}{c.end_date ? ` t/m ${fmtDate(c.end_date)}` : ""}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Getekende overeenkomsten */}
              {d.agreements.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs font-semibold text-slate-500 mb-2">Getekende overeenkomsten</div>
                  <div className="space-y-2">
                    {d.agreements.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-lg border p-3 text-sm" data-testid={`portal-agreement-${a.id}`}>
                        <div><div className="font-medium text-slate-800">{a.title}</div><div className="text-xs text-slate-500">Getekend {fmtDate(a.signed_at)}</div></div>
                        <a href={a.uploaded_pdf_url ? a.uploaded_pdf_url : `${API}/public/agreements/${a.token}/pdf`} target="_blank" rel="noreferrer"
                           data-testid={`portal-agreement-pdf-${a.id}`}
                           className="inline-flex items-center gap-1 text-[#0F1E3D] hover:text-[#C9A227] text-sm font-medium"><Download className="h-4 w-4" /> PDF</a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Betaaltermijnen */}
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Betaaltermijnen</div>
                {sorted.length === 0 ? (
                  <p className="text-sm text-slate-400">Nog geen termijnen.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Maand</TableHead><TableHead>Vervaldatum</TableHead><TableHead>Bedrag</TableHead><TableHead>Boete</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {sorted.map((p) => (
                          <TableRow key={p.id} data-testid={`portal-payment-${p.id}`}>
                            <TableCell className="font-medium">{monthLabel(p.period)}</TableCell>
                            <TableCell>{fmtDate(p.due_date)}</TableCell>
                            <TableCell className="tabular-nums">{fmtEur(p.amount)}</TableCell>
                            <TableCell className={p.penalty > 0 ? "text-red-600 tabular-nums" : "text-slate-400"}>{p.penalty > 0 ? fmtEur(p.penalty) : "—"}</TableCell>
                            <TableCell><Badge className={`${(STATUS[p.status] || STATUS.open).c} hover:${(STATUS[p.status] || STATUS.open).c}`}>{(STATUS[p.status] || STATUS.open).l}</Badge></TableCell>
                            <TableCell className="text-right">
                              {p.online && p.online.status !== "paid" ? (
                                <span className="text-xs text-amber-600 font-medium" data-testid={`portal-online-pending-${p.id}`}>● Betaling bezig ({p.online.method || "iDEAL"})</span>
                              ) : p.online && p.online.status === "paid" ? (
                                <div className="flex items-center justify-end gap-2">
                                  <span className="text-xs text-emerald-600 font-medium" data-testid={`portal-online-paid-${p.id}`}>● Online betaald</span>
                                  {p.online.session_id && (
                                    <a href={`${API}/portal/receipt/${p.online.session_id}/pdf`} target="_blank" rel="noreferrer" data-testid={`portal-receipt-${p.id}`} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Bewijs</a>
                                  )}
                                </div>
                              ) : payable(p.status) && (
                                <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" disabled={paying === p.id} onClick={() => payOnline(p)} data-testid={`portal-pay-${p.id}`}>
                                  <Banknote className="h-4 w-4 mr-1" /> {paying === p.id ? "Bezig…" : "Betaal met iDEAL"}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
        <p className="text-center text-xs text-slate-400 pb-4">Praktisch-Debiteurenbeheer · Dit portaal is alleen-lezen. Voor vragen neemt u contact op met de beheerder.</p>
      </div>

      {/* Betaling melden dialog */}
      <Dialog open={!!announceFor} onOpenChange={(o) => !o && setAnnounceFor(null)}>
        <DialogContent data-testid="portal-announce-dialog">
          <DialogHeader><DialogTitle>Betaling melden</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Meld een betaling die u heeft gedaan of gaat doen. De beheerder verwerkt deze na ontvangst.</p>
            {announceFor && (announceFor.payments || []).some((p) => ["open", "late", "partial"].includes(p.status)) && (
              <div>
                <Label>Termijn (optioneel)</Label>
                <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                  <SelectTrigger data-testid="portal-announce-period"><SelectValue placeholder="Kies een termijn" /></SelectTrigger>
                  <SelectContent>
                    {announceFor.payments.filter((p) => ["open", "late", "partial"].includes(p.status)).sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")).map((p) => (
                      <SelectItem key={p.id} value={p.period}>{monthLabel(p.period)} — {fmtEur(p.amount)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Bedrag (€)</Label><Input type="number" step="0.01" min="0" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="portal-announce-amount" /></div>
              <div><Label>Datum</Label><DateField testId="portal-announce-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
            </div>
            <div><Label>Notitie (optioneel)</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="portal-announce-note" placeholder="Bijv. betaald via overboeking op ..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnounceFor(null)}>Annuleren</Button>
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={submitAnnounce} disabled={busy} data-testid="portal-announce-submit">{busy ? "Bezig…" : "Betaling melden"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
